import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { shouldShowFormType } from '../../../components/BridgeClub/feed/feedConstants';
import { resolveFeedProduct } from '../../../components/BridgeClub/feed/resolveFeedProduct';
import FeedProductPicker, {
  FeedSelection, emptyFeedSelection, isFeedSelectionValid,
} from '../../../components/BridgeClub/feed/FeedProductPicker';
import AddFeedDialog from '../../../components/BridgeClub/feed/AddFeedDialog';
import {
  OnbAnimal, OnbBin, buildPlan, isStepComplete, defaultBinName, recommendedFeedWords,
  CATEGORY_LABELS, CategoryPlan,
} from '../../../components/BridgeClub/feed/binPlanning';
import { parseApiError } from '../../../utils/apiErrors';

const RED = '#B91C1C';

interface Props {
  onBack: () => void;
  onFinish: () => void;
  finishLabel: string;
  finishing: boolean;
}

const primaryBtn = (enabled: boolean): React.CSSProperties => ({
  padding: '10px 20px', border: 'none', borderRadius: 8,
  background: enabled ? RED : '#9ca3af', color: '#fff', fontWeight: 700,
  cursor: enabled ? 'pointer' : 'default',
});
const secondaryBtn: React.CSSProperties = {
  padding: '10px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer',
};
const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 12,
};

// Create a bin, link its animals, and add its feed — in one go. Client-sequenced (Option A):
// a mid-sequence failure leaves an incomplete bin, which the completion invariant blocks
// and the review screen / re-entry then heals.
async function createBinWithFeed(
  name: string, feedCategory: string, animalIds: number[], sel: FeedSelection,
): Promise<string | null> {
  const requiresForm = shouldShowFormType(feedCategory);
  // RESOLVE BEFORE CREATING ANYTHING. Adoption is the only step that can fail
  // for a reason the customer needs to hear, and doing it first means a failure
  // leaves no half-built bin behind.
  const { feedProductId, notice } = await resolveFeedProduct(sel);
  const bin = await bridgeClubAPI.createFeedBin({
    name: name.trim() || defaultBinName(feedCategory),
    feed_category: feedCategory,
    form_type: requiresForm && sel.formType ? sel.formType : null,
  });
  for (const id of animalIds) {
    await bridgeClubAPI.linkAnimalToBin(bin.id, { animal_id: id });
  }
  await bridgeClubAPI.addFeedToBin(bin.id, {
    lbs: parseFloat(sel.amount),
    product_name: sel.productName || 'Manual Addition',
    form_type: requiresForm ? sel.formType : undefined,
    // THE WHOLE REASON A BIN IS DELIVERABLE. Omitting this is what left bins
    // with a null feed_product and a free-text name - the product was picked
    // correctly and then thrown away on the way to the server.
    feed_product_id: feedProductId,
  });
  return notice;
}

// Display-only heading names: UPPERCASE each animal's name for the heading, or fall back to
// a count past a threshold so a long list never runs off the screen. This never touches the
// STORED name — a.name is only READ to build the heading string, never written.
const HEADING_NAME_LIMIT = 3;
function headingNames(list: OnbAnimal[], category: string): string {
  const names = list.map((a) => (a.name || a.animal_type).toUpperCase());
  if (names.length === 0) return '';
  if (names.length > HEADING_NAME_LIMIT) {
    return `your ${names.length} ${(CATEGORY_LABELS[category] || category).toUpperCase()}`;
  }
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// NO "BIN" IN THE SETUP (Brad, 2026-09-24, watching his sister sign up: "she was
// questioning what is a bin... rather than calling it a bin for the dogs and cats,
// perhaps we just ask the question, do these dogs eat the same food or are they eating
// different food? If they all eat the same, then we will put them into the same bin.
// But it won't necessarily call it a bin for them"). The customer answers about food;
// the bin is our word, made behind the answer. Dogs and cats eat food; the rest eat feed.
function foodWord(category: string): string {
  return category === 'dog' || category === 'cat' ? 'food' : 'feed';
}

// ── One category's builder ────────────────────────────────────────────────────────────
// Resolves a single category of unlinked animals into one or more bins.
const CategoryBuilder: React.FC<{ plan: CategoryPlan; onCommitted: () => Promise<void> }> =
  ({ plan, onCommitted }) => {
    const label = CATEGORY_LABELS[plan.category] || plan.category;
    const single = plan.animals.length === 1;

    // Mode: 'existing' offers re-entry bins; 'ask' asks same/different; 'same' one bin;
    // 'split' assigns subsets to bins.
    const initialMode = plan.existingBins.length > 0 ? 'existing' : (single ? 'same' : 'ask');
    const [mode, setMode] = useState<'existing' | 'ask' | 'same' | 'split'>(initialMode);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // NOT an error - the bin was created and the animals are on it. It says the
    // BAG needs setting up before we can deliver it, which is a thing the
    // customer should be told plainly rather than discovering two screens later
    // as "we don't recognise this bin's feed".
    const [notice, setNotice] = useState('');

    // A new group starts at the top of the page, not where the last Create bin left
    // the scroll (Brad, 2026-09-23).
    useEffect(() => { window.scrollTo({ top: 0 }); }, []);

    // KEYBOARD "NEXT" FROM THE BIN NAME (Brad, 2026-09-24): "it would be good if the view
    // scrolled with the next button" - the feed search sat behind the keyboard. Enter,
    // or the keyboard's next key, puts the cursor in the search box and brings it up.
    const focusFeedSearch = () => {
      const box = document.querySelector<HTMLInputElement>('.product-search');
      if (!box) return;
      box.focus();
      if (typeof box.scrollIntoView === 'function') box.scrollIntoView({ block: 'center', behavior: 'smooth' });
    };

    // 'same' state
    const [name, setName] = useState(defaultBinName(plan.category));
    const [sel, setSel] = useState<FeedSelection>(emptyFeedSelection);

    // 'split' state: a shrinking pool, and the subset selected for the current sub-bin.
    const [pool, setPool] = useState<OnbAnimal[]>(plan.animals);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [splitIndex, setSplitIndex] = useState(1);

    // The server's reason, in the customer's words, with the friendly line as the
    // fallback rather than the only outcome (utils/apiErrors.ts).
    const fail = (e: any, fallback: string) =>
      setError(parseApiError(e, { fallback }).message);

    // Re-entry: link ALL of this category's new animals into an existing bin. No new bin.
    const linkToExisting = async (binId: number) => {
      setBusy(true); setError('');
      try {
        for (const a of plan.animals) {
          await bridgeClubAPI.linkAnimalToBin(binId, { animal_id: a.id });
        }
        await onCommitted();
      } catch (e) { fail(e, 'Could not add your animals there. Please try again.'); }
      finally { setBusy(false); }
    };

    const commitSame = async () => {
      if (!isFeedSelectionValid(sel, plan.category)) return;
      setBusy(true); setError('');
      try {
        const said = await createBinWithFeed(
          name, plan.category, plan.animals.map((a) => a.id), sel);
        if (said) setNotice(said);
        await onCommitted();
      } catch (e) { fail(e, 'Could not save that. Please try again.'); }
      finally { setBusy(false); }
    };

    const commitSplitBin = async () => {
      if (selectedIds.size === 0 || !isFeedSelectionValid(sel, plan.category)) return;
      setBusy(true); setError('');
      try {
        // ONE ANIMAL, ONE BIN. Each animal is placed in exactly one bin and removed from
        // the pool, so it can never be linked twice. Do NOT add multi-bin support here:
        // process_daily_consumption OVERWRITES every FeedBinAnimal link of an animal to
        // that animal's full consumption, so an animal in two bins is double-counted
        // permanently (both bins drain at full rate). See FeedBin.active_animal_links.
        const ids = Array.from(selectedIds);
        const said = await createBinWithFeed(name, plan.category, ids, sel);
        if (said) setNotice(said);
        const remaining = pool.filter((a) => !selectedIds.has(a.id));
        if (remaining.length === 0) {
          await onCommitted();
        } else {
          setPool(remaining);
          setSelectedIds(new Set());
          setSel(emptyFeedSelection);
          setSplitIndex((n) => n + 1);
          // Back to the top for the next bin (Brad, 2026-09-24): "it kept me at the bottom".
          window.scrollTo({ top: 0 });
          setName(`${defaultBinName(plan.category)} ${splitIndex + 1}`);
        }
      } catch (e) { fail(e, 'Could not save that. Please try again.'); }
      finally { setBusy(false); }
    };

    const toggle = (id: number) => setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

    const errorBar = (
      <>
        {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}
        {notice && (
          <div style={{
            color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d',
            borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 14,
          }}>
            {notice}
          </div>
        )}
      </>
    );

    if (mode === 'existing') {
      return (
        <div>
          <h2 style={{ color: RED, marginBottom: 4, fontSize: 22 }}>What {plan.animals.length === 1 ? 'does' : 'do'} {headingNames(plan.animals, plan.category)} eat?</h2>
          <p style={{ color: '#555', marginTop: 0 }}>
            You already told us what your {label} eat. The same {foodWord(plan.category)}, or something different?
          </p>
          {errorBar}
          {plan.existingBins.map((b) => (
            <div key={b.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <strong>{b.name}</strong>
                <button onClick={() => linkToExisting(b.id)} disabled={busy} style={primaryBtn(!busy)}>
                  {busy ? 'Adding…' : 'The same as this'}
                </button>
              </div>
            </div>
          ))}
          <button onClick={() => setMode(single ? 'same' : 'ask')} disabled={busy} style={secondaryBtn}>
            Something different
          </button>
        </div>
      );
    }

    if (mode === 'ask') {
      return (
        <div>
          <h2 style={{ color: RED, marginBottom: 4, fontSize: 22 }}>Do {headingNames(plan.animals, plan.category)} eat the same {foodWord(plan.category)}?</h2>
          <p style={{ color: '#555', marginTop: 0 }}>
            If they share one {foodWord(plan.category)}, we keep track of it together. If not, we'll ask about each.
          </p>
          {errorBar}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => setMode('same')} style={primaryBtn(true)}>Yes — the same</button>
            <button
              onClick={() => { setMode('split'); setName(`${defaultBinName(plan.category)} 1`); }}
              style={secondaryBtn}
            >
              No — different
            </button>
          </div>
        </div>
      );
    }

    if (mode === 'same') {
      return (
        <div>
          <h2 style={{ color: RED, marginBottom: 4, fontSize: 22 }}>
            {single
              ? `What does ${headingNames(plan.animals, plan.category)} eat?`
              : `What do ${headingNames(plan.animals, plan.category)} eat?`}
          </h2>
          <p style={{ color: '#555', marginTop: 0 }}>
            Pick the {foodWord(plan.category)}. We'll keep track of it for you.
          </p>
          {errorBar}
          <div style={card}>
            <div className="form-group">
              <label>Name for this {foodWord(plan.category)}</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} enterKeyHint="next" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusFeedSearch(); } }} />
            </div>
            <FeedProductPicker feedCategory={plan.category} value={sel} onChange={setSel} formFilter recommended={recommendedFeedWords(plan.animals)} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={commitSame}
              disabled={busy || !isFeedSelectionValid(sel, plan.category)}
              style={primaryBtn(!busy && isFeedSelectionValid(sel, plan.category))}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      );
    }

    // mode === 'split'
    return (
      <div>
        <h2 style={{ color: RED, marginBottom: 4, fontSize: 22 }}>Who eats the same {foodWord(plan.category)}?</h2>
        <p style={{ color: '#555', marginTop: 0 }}>
          Check the ones that eat the same {foodWord(plan.category)}, then pick it. We'll ask again until
          everyone's covered ({pool.length} left).
        </p>
        {errorBar}
        <div style={card}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {pool.map((a) => (
              <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={selectedIds.has(a.id)} onChange={() => toggle(a.id)} />
                {a.name || a.animal_type}
              </label>
            ))}
          </div>
          <div className="form-group">
            <label>Name for this {foodWord(plan.category)}</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} enterKeyHint="next" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusFeedSearch(); } }} />
          </div>
          <FeedProductPicker key={splitIndex} feedCategory={plan.category} value={sel} onChange={setSel} formFilter
            recommended={recommendedFeedWords(selectedIds.size > 0 ? pool.filter((a) => selectedIds.has(a.id)) : pool)} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={commitSplitBin}
            disabled={busy || selectedIds.size === 0 || !isFeedSelectionValid(sel, plan.category)}
            style={primaryBtn(!busy && selectedIds.size > 0 && isFeedSelectionValid(sel, plan.category))}
          >
            {busy ? 'Saving…' : (pool.length === selectedIds.size ? 'Save & finish' : 'Save & next')}
          </button>
        </div>
      </div>
    );
  };

// ── The step ──────────────────────────────────────────────────────────────────────────
const SetupBinsStep: React.FC<Props> = ({ onBack, onFinish, finishLabel, finishing }) => {
  const [animals, setAnimals] = useState<OnbAnimal[]>([]);
  const [bins, setBins] = useState<OnbBin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [repairBin, setRepairBin] = useState<OnbBin | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, bRes] = await Promise.all([bridgeClubAPI.getAnimals(), bridgeClubAPI.getFeedBins()]);
      setAnimals(Array.isArray(a) ? a : []);
      setBins((bRes.bins || []) as OnbBin[]);
    } catch {
      setError('Could not load your setup.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const navigate = useNavigate();

  if (loading) return <div style={{ padding: 24, color: '#666', textAlign: 'center' }}>Loading your setup…</div>;

  const plan = buildPlan(animals, bins);
  const complete = isStepComplete(animals, bins);
  const emptyBins = bins.filter((b) => Number(b.current_lbs) <= 0);         // partial-failure repair
  const unlinkedBins = bins.filter((b) => (b.animal_links?.length || 0) === 0);

  // PHASE B IS READ-ONLY, and that is the whole gap. Once every animal is
  // placed, this screen lists bins and offers nothing but "Add feed" on an
  // EMPTY one. A customer who put two animals in one bin by mistake, or picked
  // the wrong food, has no way forward and no way back.
  //
  // The Feed tab already does all of it - create bin, delete bin, link and
  // unlink an animal, a real move between bins, and AddFeedDialog with the
  // search-and-scan picker. Rebuilding any of that here would be a second
  // implementation of a working 1,100-line screen, and every future bin fix
  // would have to land in both.
  //
  // So: send them there. OnboardingResumeBanner is on every layout page and
  // says "Next: Set up your feed bins" with a button back into this flow, so
  // the round trip already works without anything else being built.
  const manageBinsLink = (
    <div style={{ ...card, background: '#f9fafb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <strong style={{ fontSize: 14 }}>Need to change something?</strong>
          <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
            Move an animal, or change what one of them is fed.
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/bridge-club/feed')}
          style={{ ...secondaryBtn, minHeight: 44 }}
        >
          Manage feed
        </button>
      </div>
    </div>
  );

  const wrap = (children: React.ReactNode) => (
    <div style={{ maxWidth: 620, margin: '0 auto', padding: '0 16px 24px' }}>
      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}
      {children}
    </div>
  );

  // Still animals to place -> resolve the first remaining category.
  if (plan.length > 0) {
    return wrap(
      <>
        {/* KEYED PER GROUP (Brad, 2026-09-23): without the key the editor was reused
            from one animal group to the next, so Betty's bin name carried over to
            the dogs' bin. A fresh editor names itself for its own animals. */}
        <CategoryBuilder key={plan[0].category} plan={plan[0]} onCommitted={load} />
        <div style={{ marginTop: 20 }}>
          <button onClick={onBack} style={secondaryBtn}>← Back</button>
        </div>
      </>
    );
  }

  // Every animal is placed. Review + heal any empty/unlinked bin, then finish.
  return wrap(
    <>
      <h2 style={{ color: RED, marginBottom: 4, fontSize: 22 }}>What everyone eats</h2>
      <p style={{ color: '#555', marginTop: 0 }}>Here's your setup.</p>
      {bins.map((b) => {
        const empty = Number(b.current_lbs) <= 0;
        const noLink = (b.animal_links?.length || 0) === 0;
        return (
          <div key={b.id} style={{ ...card, borderColor: (empty || noLink) ? '#fca5a5' : '#e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div>
                <strong>{b.name}</strong>
                <div style={{ fontSize: 13, color: '#555' }}>
                  {(b.animal_links || []).map((l) => l.animal_name).join(', ') || 'No animals linked'}
                  {' · '}{Number(b.current_lbs) > 0 ? `${Number(b.current_lbs).toFixed(0)} lbs` : 'no feed yet'}
                </div>
              </div>
              {empty && (
                <button onClick={() => setRepairBin(b)} style={primaryBtn(true)}>Add feed</button>
              )}
            </div>
          </div>
        );
      })}

      {manageBinsLink}

      {emptyBins.length > 0 && (
        <p style={{ color: '#b45309', fontSize: 13 }}>Add feed to each one to finish.</p>
      )}
      {unlinkedBins.length > 0 && (
        <p style={{ color: '#b45309', fontSize: 13 }}>Each one needs an animal on it.</p>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <button onClick={onBack} style={secondaryBtn}>← Back</button>
        <button onClick={onFinish} disabled={!complete || finishing} style={primaryBtn(complete && !finishing)}>
          {finishing ? 'Finishing…' : finishLabel}
        </button>
      </div>

      {repairBin && (
        <AddFeedDialog
          bin={repairBin}
          onClose={() => setRepairBin(null)}
          onAdded={() => { setRepairBin(null); load(); }}
          onError={setError}
        />
      )}
    </>
  );
};

export default SetupBinsStep;
